/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as olStyle from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import VectorTileSource from 'ol/source/VectorTile.js';
import Tile from 'ol/Tile';
import Feature from 'ol/Feature';
import VectorSource from 'ol/source/Vector';
import { GeoJsonUtils } from './OlGeojsonUtils';
import { FileServiceClient } from '@shared/files/FileServiceClient';
import { GeojsonFeatureCollection } from '@shared/geojson/types';
import geojsonvt from 'geojson-vt';
import { extentFromProjection, createXYZ } from 'ol/tilegrid';
import Projection from 'ol/proj/Projection';
import Point from 'ol/geom/Point';

const styleCache: { [key: string]: olStyle.Style } = {};
const hatchCache: { [key: string]: olStyle.Fill } = {};

const textStroke = new olStyle.Stroke({
    color: [0, 0, 0, 0.7],
});

const createTextStylesAlongLine = (
    feature: Feature,
    text: string,
    fontSize: number,
    fontFamily: string,
    repeatDistance: number,
    offsetInPattern: number,
    offsetY: number,
    rotation: number,
    rgb: string
): olStyle.Style[] => {
    const geometry = feature.getGeometry();
    if (!geometry) {
        return [];
    }

    const geometryType = geometry.getType();
    if (geometryType !== 'LineString' && geometryType !== 'MultiLineString') {
        return [];
    }

    const coordinates = GeoJsonUtils.getCoordinatesFromGeometry(geometry);
    if (!coordinates) {
        return [];
    }

    const lineLength = GeoJsonUtils.getLineLength(coordinates);
    const styles: olStyle.Style[] = [];
    let currentDistance = offsetInPattern;

    while (currentDistance < lineLength) {
        const positionInfo = GeoJsonUtils.getPointAtDistance(coordinates, currentDistance);

        if (positionInfo) {
            const { point, angle } = positionInfo;
            // Make text upright
            const textAngle = GeoJsonUtils.normalizeTextAngle(angle + rotation);

            const offsetPoint = [point[0] + Math.cos(angle) * offsetY, point[1] + Math.sin(angle) * offsetY];

            styles.push(
                new olStyle.Style({
                    geometry: new Point(offsetPoint),
                    text: new olStyle.Text({
                        font: `${fontSize}px ${fontFamily}`,
                        text: text,
                        textAlign: 'center',
                        textBaseline: 'middle',
                        rotation: textAngle,
                        rotateWithView: true,
                        stroke: textStroke,
                        fill: new olStyle.Fill({
                            color: `rgba(${rgb},0.7)`,
                        }),
                    }),
                })
            );
        }

        currentDistance += repeatDistance;
    }

    return styles;
};

export enum RotationUnit {
    RADIANS = 0,
    DEGREES = 1,
}

const polyStyle = (feature: Feature, resolution: number): olStyle.Style | olStyle.Style[] => {
    const rgb = feature.get('color') || '0,0,0';
    const alpha = feature.get('alpha') || '1.0';
    const resolutionKey = resolution.toFixed(5);
    const weight = feature.get('weight') || 9;
    let width = feature.get('width') || 0;
    const hatchKey = hatchHashcode(feature);
    const lineDashKey = feature.get('line_dash') || 0;

    const lineDashResult = lineDashArray(feature, resolution);
    const lineDash = lineDashResult.pattern;
    const lineDashText = lineDashResult.textInfo;
    const hasText = lineDashText !== null;

    let cacheKey = rgb + ',' + alpha + ';' + weight;
    if (width !== 0 || hatchKey !== '' || lineDashKey !== 0) {
        cacheKey = cacheKey + ';' + resolutionKey + ';' + width + ';' + hatchKey + ';' + lineDashKey;
    }

    let style = !hasText ? styleCache[cacheKey] : null;

    if (!style) {
        let stroke: olStyle.Stroke;
        let fill: olStyle.Fill;

        if (hatchKey === '') {
            fill = null;
        } else if (hatchKey === 'solid') {
            fill = new olStyle.Fill({
                color: `rgba(${rgb},1.0)`,
            });
        } else {
            const canvasKey = resolutionKey + ';' + hatchKey + ';' + rgb;
            fill = hatchCache[canvasKey];

            if (!fill) {
                fill = createFillPattern(feature, resolution);
                hatchCache[canvasKey] = fill;
            }
        }

        if (width === 0) {
            width = Math.max(1.2, weight / 20);
        } else {
            const mercatorScale = GeoJsonUtils.calculateScaleForPoint(feature);
            width = Math.max(1.2, width / mercatorScale / resolution);
        }

        if (hatchKey !== '' && hatchKey !== 'solid') {
            // hatch boundaries are invisible
            stroke = null;
        } else {
            stroke = new olStyle.Stroke({
                lineCap: 'round',
                lineDash: lineDash,
                lineJoin: 'round',
                color: `rgba(${rgb},${alpha})`,
                width: width,
            });
        }

        style = new olStyle.Style({
            stroke: stroke,
            fill: fill,
        });

        if (!hasText) {
            styleCache[cacheKey] = style;
        }
    }
    // Lets not load text if zoomed out too much
    if (lineDashText && resolution < 0.18) {
        const mercatorScale = GeoJsonUtils.calculateScaleForPoint(feature);
        // Scale the text height by half to avoid overlapping text
        const textHeightPixels = (lineDashText.scale * mercatorScale) / resolution / 2;

        const repeatDistance = lineDashText.patternLength * mercatorScale;

        const textWidth = lineDashText.text.length * lineDashText.scale * 0.6;
        const textOffsetInPattern = (lineDashText.textPositionInPattern + lineDashText.offsetX + textWidth / 2) * mercatorScale;
        const offsetY = -(lineDashText.offsetY * mercatorScale);

        const textStyles = createTextStylesAlongLine(
            feature,
            lineDashText.text,
            textHeightPixels,
            lineDashText.style || 'Arial',
            repeatDistance,
            textOffsetInPattern,
            offsetY,
            lineDashText.rotation,
            rgb
        );

        return [style, ...textStyles];
    }

    return style;
};

const textStyle = (feature: Feature, resolution: number): olStyle.Style => {
    const rgb = feature.get('COLOR') || '0,0,0';
    const height = feature.get('HEIGHT') || 1;
    const font = feature.get('font') || 'Courier New';
    const textOnly = feature.get('TEXT_ONLY') || false;

    // Do not apply the empty style for big texts (>50m in height)
    if (height / resolution < 3 && resolution > 0.01 && height < 50 && !textOnly) {
        return new olStyle.Style();
    }

    const mercatorScale = GeoJsonUtils.calculateScaleForPoint(feature);
    const content: string = feature.get('CONTENT');
    const rotationWithoutCorrection = feature.get('ROTATION') || 0;
    const valign = feature.get('VALIGN') || 0;
    const halign = feature.get('HALIGN') || 0;

    const rotationUnit: RotationUnit = feature.get('ROTATION_UNITS');

    /*
        Note: After switching to ODA DWG parser all the rotations are in radians.
        Also ODA sees the rotation in different direction than our old parser so we have to negate the rotation.
        We do the negation only for the ODA parsed drawings, the legacy ones wont need it. 
    */
    const rotation = rotationUnit === RotationUnit.RADIANS ? -rotationWithoutCorrection : rotationWithoutCorrection * (Math.PI / 180);

    let baseline: CanvasTextBaseline = 'alphabetic';
    if (valign === 1) {
        baseline = 'bottom';
    } else if (valign === 2) {
        baseline = 'middle';
    } else if (valign === 3) {
        baseline = 'top';
    }

    let align: CanvasTextAlign = 'left';
    if (halign === 1) {
        align = 'center';
    } else if (halign === 2) {
        align = 'right';
    }

    const fill = new olStyle.Fill({
        color: `rgba(${rgb},0.7)`,
    });

    return new olStyle.Style({
        text: new olStyle.Text({
            font: 'lighter ' + (height * mercatorScale) / resolution + 'px ' + font,
            rotateWithView: true,
            text: content,
            textAlign: align,
            textBaseline: baseline,
            rotation: rotation,
            stroke: textStroke,
            fill: fill,
        }),
    });
};

function createVectorSourceFromFeaturecollection(features: Feature[]): VectorSource {
    const vectorSource = new VectorSource({
        features: features,
        format: new GeoJSON(),
    });

    return vectorSource;
}

const projection = 'EPSG:3857';
const extent = extentFromProjection(projection);
const tileGrid = createXYZ({
    extent: extent,
    maxZoom: 22,
    tileSize: 512,
});

const VECTOR_TILE_FORMAT = new GeoJSON({
    // Data returned from geojson-vt is in tile pixel units
    dataProjection: new Projection({
        code: 'TILE_PIXELS',
        units: 'tile-pixels',
        extent: [0, 0, 4096, 4096],
    }),
});

function createVectorTileSourceFromFeatureCollection(featureCollection: GeojsonFeatureCollection): VectorTileSource {
    const tileIndex = geojsonvt(featureCollection, {
        maxZoom: 24,
        tolerance: 5,
        extent: 4096,
    });

    const vectorSource = new VectorTileSource({
        maxZoom: 24,
        tileUrlFunction: tileCoord => JSON.stringify(tileCoord),
        tileLoadFunction: (tile: Tile, url: string) => {
            const tileCoord = JSON.parse(url);

            const tileData = tileIndex.getTile(tileCoord[0], tileCoord[1], tileCoord[2]);

            const geojson = JSON.stringify(
                {
                    type: 'FeatureCollection',
                    features: tileData ? tileData.features : [],
                },
                GeoJsonUtils.replacer
            );

            const actualFeatures = VECTOR_TILE_FORMAT.readFeatures(geojson, {
                extent: tileGrid.getTileCoordExtent(tileCoord),
                featureProjection: 'EPSG:3857',
            });

            //@ts-ignore
            tile.setFeatures(actualFeatures);
        },
    });

    return vectorSource;
}

async function getGeojsonForObjectKey(objectKey: string): Promise<GeojsonFeatureCollection> {
    const bucketName: string = kuura.getServerDomain();
    const downloadUrl: string = await FileServiceClient.getDownloadUrl(objectKey, bucketName);

    const response = await fetch(downloadUrl);

    if (!response.ok) {
        return null;
    }

    const featureCollection = await response.json();
    return featureCollection;
}

interface LineDashTextInfo {
    text: string;
    style?: string; // Text style name (e.g., "STANDARD")
    scale: number; // S parameter - text height in drawing units
    rotation: number; // R or U parameter - rotation in radians
    offsetX: number; // X parameter - offset along line direction
    offsetY: number; // Y parameter - perpendicular offset
    patternLength: number; // Total length of the repeating pattern
    textPositionInPattern: number; // Where in the pattern the text appears (accumulated length before text)
    textInsertIndex: number; // Index in the dash array where text element should be inserted
}

interface LineDashResult {
    pattern: number[] | null;
    textInfo: LineDashTextInfo | null;
}

const lineDashArray = (feature: Feature, resolution: number): LineDashResult => {
    const lineDash = feature.get('line_dash') || null;
    if (!lineDash) {
        return { pattern: null, textInfo: null };
    }

    const mercatorScale = GeoJsonUtils.calculateScaleForPoint(feature);

    // Check if this is a complex pattern (starts with "A,")
    // Format: A,5.0,-0.25,["BT",Lignes,S=1.0,R=0.0,X=0.0,Y=-0.18],-1.25
    let pattern: string[];
    let textInfo: LineDashTextInfo | null = null;
    let patternValues: number[] = [];
    let textInsertIndex = -1;

    if (lineDash.startsWith('A,')) {
        const parts: string[] = [];
        let currentPart = '';
        let insideBrackets = false;
        let bracketContent = '';
        let textPositionInPattern = 0;

        const addPatternValue = (part: string, beforeText: boolean) => {
            const trimmed = part.trim();
            if (!trimmed) return;

            const val = parseFloat(trimmed);
            parts.push(trimmed);

            if (!isNaN(val)) {
                patternValues.push(val);
                if (beforeText) {
                    textPositionInPattern += Math.abs(val);
                }
            }
        };

        for (let i = 2; i < lineDash.length; i++) {
            const char = lineDash[i];

            if (char === '[') {
                insideBrackets = true;
                bracketContent = '';
                addPatternValue(currentPart, true);
                currentPart = '';
                textInsertIndex = parts.length;
            } else if (char === ']') {
                insideBrackets = false;
                if (bracketContent) {
                    textInfo = parseLineDashText(bracketContent);
                    if (textInfo) {
                        textInfo.textPositionInPattern = textPositionInPattern;
                        textInfo.textInsertIndex = textInsertIndex;
                    }
                }
            } else if (char === ',' && !insideBrackets) {
                addPatternValue(currentPart, !textInfo);
                currentPart = '';
            } else if (!insideBrackets) {
                currentPart += char;
            } else {
                bracketContent += char;
            }
        }

        addPatternValue(currentPart, false);
        pattern = parts;

        if (textInfo) {
            textInfo.patternLength = patternValues.reduce((sum, val) => sum + Math.abs(val), 0);
        }
    } else {
        pattern = lineDash.split(',');
    }

    // Calculate line width for scaling
    let width = feature.get('width') || 0;
    const weight = feature.get('weight') || 9;
    if (width === 0) {
        width = Math.max(1.2, weight / 20);
    } else {
        width = Math.max(1.2, width / mercatorScale / resolution);
    }

    // Constants for dash pattern scaling
    const DASH_MIN_MULTIPLIER = 2; // Minimum visibility for dashes
    const GAP_MIN_MULTIPLIER = 2; // Minimum visibility for gaps
    const GAP_SCALE_DIVISOR = 6; // Scale down gaps to keep pattern tight

    // Build the dash pattern array, inserting gaps where text appears
    const result: number[] = [];

    for (let i = 0; i < pattern.length; i++) {
        // If this is where the text should be inserted, add a gap for it
        if (textInfo && i === textInfo.textInsertIndex) {
            const textWidth = (textInfo.text.length * textInfo.scale * 0.6 * mercatorScale) / resolution;
            result.push(0); // Gap before text
            result.push(textWidth); // Space for text (renders as gap in line)
            result.push(0); // Gap after text
        }

        const parsedValue = parseFloat(pattern[i]);
        const value = Math.abs(parsedValue);
        const isDash = parsedValue >= 0;

        if (isNaN(value)) {
            result.push(0);
        } else if (value === 0) {
            result.push(isDash ? width * DASH_MIN_MULTIPLIER : 0);
        } else {
            const scaledValue = value / mercatorScale / resolution;
            if (isDash) {
                result.push(Math.max(width * DASH_MIN_MULTIPLIER, scaledValue));
            } else {
                result.push(Math.max(width * GAP_MIN_MULTIPLIER, scaledValue / GAP_SCALE_DIVISOR));
            }
        }
    }

    return { pattern: result, textInfo };
};

const parseLineDashText = (bracketContent: string): LineDashTextInfo | null => {
    // Parse content like: "BT",Lignes,S=1.0,R=0.0,X=0.0,Y=-0.18
    const parts = bracketContent.split(',');
    if (!parts.length) return null;

    const text = parts[0].replace(/["']/g, '').trim();
    if (!text) return null;

    let styleIndex = 1;
    let styleName: string | undefined;
    if (parts.length > 1 && !parts[1].includes('=')) {
        styleName = parts[1].trim();
        styleIndex = 2;
    }

    const params = { scale: 1.0, rotation: 0.0, offsetX: 0.0, offsetY: 0.0 };

    // Parse S, R/U, X, Y parameters
    for (let i = styleIndex; i < parts.length; i++) {
        const param = parts[i].trim();
        const [key, value] = param.split('=');
        const numValue = parseFloat(value);

        if (!isNaN(numValue)) {
            if (key === 'S') params.scale = numValue;
            else if (key === 'R' || key === 'U') params.rotation = numValue;
            else if (key === 'X') params.offsetX = numValue;
            else if (key === 'Y') params.offsetY = numValue;
        }
    }

    return {
        text,
        style: styleName,
        ...params,
        patternLength: 0,
        textPositionInPattern: 0,
        textInsertIndex: -1,
    };
};

const hatchHashcode = (feature: Feature): string => {
    const solid = feature.get('solid') || 0;

    if (solid) {
        return 'solid';
    }

    const hatches = feature.get('hatch_lines') || 0;

    if (hatches > 0) {
        let key = '';

        for (let i = 0; i < hatches; i++) {
            const a = feature.get('hatch' + i + '_a');
            const be = feature.get('hatch' + i + '_base_e');
            const bn = feature.get('hatch' + i + '_base_n');
            const d = feature.get('hatch' + i + '_delta');

            key = key + '\n' + i + ' a: ' + a + ' base_e: ' + be + ' base_n: ' + bn + ' delta: ' + d;
        }

        return utils.hashCode(key);
    }

    return '';
};

const createFillPattern = (feature: Feature, resolution: number): olStyle.Fill => {
    const size = 512;
    const d = Math.sqrt(size * size + size * size);
    const rgb = feature.get('color') || '0,0,0';
    const hatches = feature.get('hatch_lines') || 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = size;
    canvas.height = size;

    ctx.lineWidth = 1.2;
    ctx.strokeStyle = `rgb(${rgb})`;

    for (let i = 0; i < hatches; i++) {
        const a = Number(feature.get('hatch' + i + '_a') || 0.785398163);
        const b = a + Math.PI;
        //const be = Number(feature.get('hatch' + i + '_base_e') || 0);
        //const bn = Number(feature.get('hatch' + i + '_base_n') || 0);
        const delta = Number(feature.get('hatch' + i + '_delta') || 3.0);
        const pixelDensity = delta / resolution;

        const ap = a + Math.PI / 2;

        // Will start to look like solid
        if (pixelDensity <= 2) {
            if (pixelDensity <= 1) {
                return new olStyle.Fill({
                    color: `rgba(${rgb},0.9)`,
                });
            } else {
                return new olStyle.Fill({
                    color: `rgba(${rgb},0.6)`,
                });
            }
        }

        const stepX = (delta * Math.cos(ap)) / resolution;
        const stepY = (delta * Math.sin(ap)) / resolution;

        const startX = stepX < 0 ? size : 0;
        const startY = stepY < 0 ? size : 0;

        let x1: number, y1: number, x2: number, y2: number;

        x1 = startX + Math.cos(a) * d;
        y1 = startY + Math.sin(a) * d;
        x2 = startX + Math.cos(b) * d;
        y2 = startY + Math.sin(b) * d;

        const lines = d / pixelDensity;
        for (let i = 0; i < lines; i++) {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();

            x1 += stepX;
            y1 += stepY;
            x2 += stepX;
            y2 += stepY;
        }
    }

    return new olStyle.Fill({
        color: ctx.createPattern(canvas, 'repeat'),
    });
};

// Might need to invalidate the caches after unloading large files
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function invalidateHatchCache() {
    if (Object.keys(hatchCache).length > 50) {
        Object.keys(hatchCache).forEach((value, index, array) => {
            if (index < 25) {
                delete hatchCache[value];
            }
        });
    }
}

export const GeoJsonFeatureFactory = {
    createVectorTileSourceFromFeatureCollection,
    createVectorSourceFromFeaturecollection,
    getGeojsonForObjectKey,

    STYLE: {
        polyStyle,
        textStyle,
    },
};
